// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useCallback, useEffect, useRef } from 'react';
import './TiptapEditor.css';
import { AppIcon, IconButton, Tooltip } from '@loa/ui-components';

export interface TiptapEditorProps {
  /**
   * Initial HTML content for the editor
   */
  content: string;
  /**
   * Callback when content changes
   */
  onChange: (html: string) => void;
  /**
   * Placeholder text when editor is empty
   */
  placeholder?: string;
  /**
   * Whether the editor is disabled
   */
  disabled?: boolean;
  /**
   * Minimum height for the editor
   */
  minHeight?: string;
  /**
   * Maximum height for the editor (enables scrolling)
   */
  maxHeight?: string;
  /**
   * ID for accessibility
   */
  id?: string;
  /**
   * ARIA label for the editor
   */
  ariaLabel?: string;
  /**
   * Hide the built-in toolbar (use when rendering EditorToolbar externally)
   */
  hideToolbar?: boolean;
  /**
   * Callback fired when the editor instance is ready
   */
  onEditorReady?: (editor: Editor) => void;
}

interface ToolbarButtonProps {
  editor: Editor | null;
  command: string;
  icon: string;
  title: string;
  isActive?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const ToolbarButton = ({
  editor,
  command,
  icon,
  title,
  isActive,
  onClick,
  disabled,
}: ToolbarButtonProps) => {
  const active = isActive ?? (editor?.isActive(command) || false);

  return (
    <Tooltip
      content={title}
      trigger={
        <IconButton
          variant="ghost"
          size="xs"
          onClick={onClick}
          disabled={disabled || !editor}
          aria-label={title}
          aria-pressed={active}
          className={active ? 'toolbar-btn--active' : ''}
          icon={<AppIcon name={icon} size={18} color={active ? '#2D0A6C' : '#525252'} />}
        />
      }
      position="top"
      size="mini"
    />
  );
};

export const EditorToolbar = ({
  editor,
  disabled,
}: {
  editor: Editor | null;
  disabled?: boolean;
}) => {
  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = globalThis.prompt('Enter URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className="tiptap-toolbar"
      role="toolbar"
      aria-label="Text formatting options"
    >
      <div className="tiptap-toolbar__group">
        <ToolbarButton
          editor={editor}
          command="undo"
          icon="undo"
          title="Undo (Ctrl+Z)"
          isActive={false}
          onClick={() => editor.chain().focus().undo().run()}
          disabled={disabled || !editor.can().undo()}
        />
        <ToolbarButton
          editor={editor}
          command="redo"
          icon="redo"
          title="Redo (Ctrl+Y)"
          isActive={false}
          onClick={() => editor.chain().focus().redo().run()}
          disabled={disabled || !editor.can().redo()}
        />
      </div>

      <div className="tiptap-toolbar__divider" />

      <div className="tiptap-toolbar__group">
        <ToolbarButton
          editor={editor}
          command="bold"
          icon="bold"
          title="Bold (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
        />
        <ToolbarButton
          editor={editor}
          command="italic"
          icon="italic"
          title="Italic (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
        />
        <ToolbarButton
          editor={editor}
          command="underline"
          icon="underline"
          title="Underline (Ctrl+U)"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={disabled}
        />
      </div>

      <div className="tiptap-toolbar__divider" />

      <div className="tiptap-toolbar__group">
        <ToolbarButton
          editor={editor}
          command="bulletList"
          icon="list ul"
          title="Bullet List"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
        />
        <ToolbarButton
          editor={editor}
          command="orderedList"
          icon="list ol"
          title="Numbered List"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
        />
      </div>

      <div className="tiptap-toolbar__divider" />

      <div className="tiptap-toolbar__group">
        <ToolbarButton
          editor={editor}
          command="link"
          icon="linkify"
          title="Insert Link"
          onClick={setLink}
          disabled={disabled}
        />
        {editor.isActive('link') && (
          <ToolbarButton
            editor={editor}
            command="unlink"
            icon="unlinkify"
            title="Remove Link"
            isActive={false}
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

/**
 * TiptapEditor - Reusable WYSIWYG editor component built on Tiptap
 *
 * Features:
 * - Bold, italic, underline formatting
 * - Bullet and numbered lists
 * - Link insertion
 * - Undo/redo
 * - Placeholder text
 * - Accessible with ARIA attributes
 */
export const TiptapEditor = ({
  content,
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  minHeight = '200px',
  maxHeight = '400px',
  id,
  ariaLabel = 'Rich text editor',
  hideToolbar = false,
  onEditorReady,
}: TiptapEditorProps) => {
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        // StarterKit bundles Link + Underline; disable them here so the
        // explicit, custom-configured extensions below are the only ones
        // registered (avoids the "Duplicate extension names" tiptap warning).
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Notify parent when editor is ready
  useEffect(() => {
    if (editor) {
      onEditorReadyRef.current?.(editor);
    }
  }, [editor]);

  // Update content when prop changes (e.g., when resetting to default)
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Update editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  return (
    <div
      style={{
        ...(!hideToolbar && { border: '1px solid #e0e0e0', borderRadius: '4px' }),
        ...(hideToolbar && { borderRadius: '0 0 16px 16px' }),
        backgroundColor: disabled ? '#F4F4F4' : '#FFFFFF',
      }}
    >
      {!hideToolbar && <EditorToolbar editor={editor} disabled={disabled} />}
      <div
        style={{
          minHeight,
          maxHeight,
          overflowY: 'auto',
          padding: '12px',
        }}
      >
        <EditorContent
          editor={editor}
          id={id}
          aria-label={ariaLabel}
          style={{
            outline: 'none',
          }}
        />
      </div>
      <style>{`
        .ProseMirror {
          outline: none;
        }
        .ProseMirror p {
          margin: 0 0 1em 0;
        }
        .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .ProseMirror ul,
        .ProseMirror ol {
          margin: 0 0 1em 0;
          padding-left: 1.5em;
        }
        .ProseMirror a {
          color: #2185d0;
          text-decoration: underline;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #adb5bd;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default TiptapEditor;
